"use client";

import { useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Progress } from "@/components/ui/progress";
import { useAssignmentHeader } from "@/context/AssignmentHeaderContext";
import NumericText from "@/components/NumericText";
import { UsersIcon } from "./icons";
import { Logo } from "@/components/Logo";
import { useLocale } from "@/hooks/use-locale";
import {
  useInterfaceLanguageOptions,
  useUiLanguageShortLabel,
} from "@/hooks/use-ui-language-options";
import { useAppSelector } from "@/store";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronsUpDown, LogOut, X, User } from "lucide-react";
import {
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
} from "@/api/base/private/axiosPrivateClient";
import { NavUserProfileDrawer } from "@/components/nav-user/NavUserProfileDrawer";
import { useIsMobile } from "@/hooks/use-mobile";
import { useUserRole } from "@/hooks/useUserRole";

type AppHeaderProps = {
  assignmentTitle?: string;
};

const AppHeader: React.FC<AppHeaderProps> = ({ assignmentTitle }) => {
  const t = useTranslations();

  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { data } = useAssignmentHeader();
  const isMobile = useIsMobile();

  // Mobile-only: language switcher + user avatar
  const { currentLocale, changeLanguage } = useLocale();
  const languageOptions = useInterfaceLanguageOptions();
  const languageShortLabel = useUiLanguageShortLabel(currentLocale);
  const { user } = useAppSelector((s) => s.auth);
  const { isStudent } = useUserRole();
  const avatarUrl = user?.photo_thumb?.medium || user?.photo_thumb?.small || user?.photo || "";
  const initials = [user?.firstname, user?.lastname]
    .filter(Boolean)
    .map((name) => name?.[0]?.toUpperCase() ?? "")
    .join("") || "?";

  const [imgError, setImgError] = useState(false);
  const [mobileUserMenuOpen, setMobileUserMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  const displayName =
    [user?.firstname, user?.lastname].filter(Boolean).join(" ") || "—";
  const displayEmail = user?.email || null;
  const iin =
    user?.student &&
    typeof user.student === "object" &&
    "iin" in user.student &&
    typeof user.student.iin === "string"
      ? user.student.iin
      : null;

  const handleLogout = () => {
    try {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    } catch {}
    window.dispatchEvent(new Event("auth-token-changed"));
    window.location.href = "/";
  };

  const isAssignmentPage = pathname.startsWith("/assignments");
  const resolvedAssignmentTitle = assignmentTitle ?? data.title;

  // /assignments/:id[/section]
  const assignmentMatch = pathname.match(/^\/assignments\/(\d+)(?:\/([^\/?#]+))?/);
  const assignmentId = assignmentMatch?.[1];
  const assignmentSection = assignmentMatch?.[2];

  const sectionLabel =
    assignmentSection === "proctoring"
      ? t("btn-assignment-proctoring")
      : assignmentSection === "testing"
        ? t("btn-assignment-testing")
        : null;

  const getBreadcrumbs = () => {
    if (pathname === "/groups") {
      const tab = searchParams.get("tab") || "groups";

      const tabLabels: Record<string, string> = {
        groups: t("label-group-list"),
        students: t("label-class-students"),
        "invite-code": t("page-invite-index"),
      };

      return (
        <BreadcrumbItem>
          <BreadcrumbPage>{tabLabels[tab] || tabLabels.groups}</BreadcrumbPage>
        </BreadcrumbItem>
      );
    }

    if (pathname === "/assignments") {
      const tab = searchParams.get("tab") || "quizs";

      const tabLabels: Record<string, string> = {
        quizs: t("page-assignment-index"),
        tests: t("ui-assignments-tab-tests"),
        certificates: t("ui-certificates-title"),
      };

      return (
        <BreadcrumbItem>
          <BreadcrumbPage>{tabLabels[tab] || tabLabels.quizs}</BreadcrumbPage>
        </BreadcrumbItem>
      );
    }

    if (isAssignmentPage) {
      return (
        <>
          <BreadcrumbItem className="hidden md:block">
            <BreadcrumbLink href="/assignments">
              {t("page-assignment-index")}
            </BreadcrumbLink>
          </BreadcrumbItem>

          {assignmentId && (
            <>
              <BreadcrumbSeparator className="hidden md:block">
                /
              </BreadcrumbSeparator>

              <BreadcrumbItem className="min-w-0">
                {sectionLabel ? (
                  <BreadcrumbLink className="truncate" href={`/assignments/${assignmentId}`}>
                    {resolvedAssignmentTitle || t("label-assignment")}
                  </BreadcrumbLink>
                ) : (
                  <BreadcrumbPage className="truncate max-w-full">
                    {resolvedAssignmentTitle || t("label-assignment")}
                  </BreadcrumbPage>
                )}
              </BreadcrumbItem>

              {sectionLabel && (
                <>
                  <BreadcrumbSeparator className="hidden md:block">
                    /
                  </BreadcrumbSeparator>
                  <BreadcrumbItem>
                    <BreadcrumbPage>{sectionLabel}</BreadcrumbPage>
                  </BreadcrumbItem>
                </>
              )}
            </>
          )}
        </>
      );
    }

    if (pathname.startsWith("/settings")) {
      return (
        <>
          <BreadcrumbItem className="hidden md:block">
            <BreadcrumbLink href="/settings">{t("btn-settings")}</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator className="hidden md:block">
            /
          </BreadcrumbSeparator>
          <BreadcrumbItem>
            <BreadcrumbPage>{t("label-proctoring-system")}</BreadcrumbPage>
          </BreadcrumbItem>
        </>
      );
    }

    return (
      <BreadcrumbItem>
        <BreadcrumbPage>{t("label-media-root")}</BreadcrumbPage>
      </BreadcrumbItem>
    );
  };

  const finished = data.progress?.done ?? 0;
  const total = data.progress?.total ?? 0;
  const percent = total > 0 ? Math.round((finished / total) * 100) : 0;
  const showAssignmentProgress =
    isAssignmentPage &&
    !!assignmentId &&
    total > 0 &&
    (!isMobile || isStudent);

  return (
    <header className="relative flex bg-white h-16 items-center">
      <div className="flex items-center gap-2 px-4 w-full">
        {/* Mobile: Logo instead of hamburger trigger — students only */}
        {isStudent && <Logo variant="dark" className="md:hidden h-7 w-auto" />}

        {/* Mobile managers keep the sidebar trigger; students keep the logo-only mobile header. */}
        <SidebarTrigger className={isStudent ? "hidden md:flex -ml-1" : "-ml-1"} />

        {/* Breadcrumbs stay desktop-only to free space for mobile account controls. */}
        <Separator orientation="vertical" className="hidden md:block data-[orientation=vertical]:h-3 data-[orientation=vertical]:w-[1.5px] bg-muted rounded-lg" />
        <Breadcrumb className="hidden md:flex flex-1 min-w-0">
          <BreadcrumbList className="min-w-0 flex-nowrap">
            {getBreadcrumbs()}
          </BreadcrumbList>
        </Breadcrumb>

        {/* Mobile spacer — students only */}
        {isStudent && <div className="md:hidden flex-1" />}

        {showAssignmentProgress && (
          <div className="ml-auto flex items-center gap-4">
            {/* Процент */}
            <div className="text-sm font-semibold text-blue-600">
              <NumericText>{percent}</NumericText>%
            </div>

            <Progress
              value={percent}
              className="w-25 h-2 bg-gray-200"
              indicatorClassName="progress-gradient"
            />

            <div className="flex items-center text-sm text-gray-600 gap-1">
              <UsersIcon />
              <NumericText className="text-[#171717] font-semibold">{finished}</NumericText><NumericText className="text-[#A1A1A1]">{t("label-of")}</NumericText><NumericText className="text-[#A1A1A1]">{total}</NumericText>
            </div>
          </div>
        )}

        {/* Mobile-only: language switcher + user avatar */}
        {isMobile && <div className="ml-auto md:hidden flex items-center gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="h-14 w-14 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                aria-label="Language"
              >
                {languageShortLabel}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="bottom" align="end" className="min-w-[140px]">
              {languageOptions.map((opt) => (
                <DropdownMenuItem
                  key={opt.locale}
                  onSelect={() => changeLanguage(opt.locale)}
                  className="cursor-pointer"
                >
                  <span className="mr-2 text-xs font-semibold text-muted-foreground min-w-[24px]">
                    {opt.short}
                  </span>
                  <span className="text-sm">{opt.label}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Mobile user avatar button */}
          <button
            type="button"
            className="flex items-center gap-[5px] bg-gradient-to-r from-[#B20BFF]/15 to-[#0059FF]/15 hover:from-[#B20BFF]/25 hover:to-[#0059FF]/25 rounded-[10px] p-[2px] transition-colors"
            aria-label="User menu"
            onClick={() => setMobileUserMenuOpen(true)}
          >
            <div className="relative h-[52px] w-[52px] rounded-[8px] bg-[#5C6370] shrink-0 overflow-hidden">
              <span className="absolute inset-0 flex items-center justify-center text-white text-base font-semibold">
                {initials}
              </span>
              {avatarUrl && !imgError && (
                <img
                  src={avatarUrl}
                  className="absolute inset-0 w-full h-full object-cover"
                  alt=""
                  onError={() => setImgError(true)}
                />
              )}
            </div>
            <ChevronsUpDown size={16} className="text-[#1a1a1a] shrink-0 mx-[1px]" />
          </button>

          {/* Mobile user menu overlay */}
          {mobileUserMenuOpen && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
              onClick={() => setMobileUserMenuOpen(false)}
            >
              <div
                className="bg-white rounded-2xl shadow-xl w-[calc(100%-2rem)] max-w-sm p-0 overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header: avatar + name + email + close */}
                <div className="flex items-center gap-3 px-4 py-4 border-b relative">
                  <div className="relative h-12 w-12 rounded-xl bg-[#5C6370] shrink-0 overflow-hidden">
                    <span className="absolute inset-0 flex items-center justify-center text-white text-base font-semibold">
                      {initials}
                    </span>
                    {avatarUrl && !imgError && (
                      <img
                        src={avatarUrl}
                        className="absolute inset-0 w-full h-full object-cover"
                        alt=""
                      />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-gray-900 truncate">{displayName}</div>
                    {displayEmail && (
                      <div className="text-xs text-gray-500 truncate">{displayEmail}</div>
                    )}
                    {!displayEmail && iin && (
                      <div className="text-xs text-gray-500 truncate">{iin}</div>
                    )}
                  </div>
                  <button
                    type="button"
                    className="shrink-0 p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                    onClick={() => setMobileUserMenuOpen(false)}
                    aria-label="Close"
                  >
                    <X size={20} />
                  </button>
                </div>

                {/* Menu items */}
                <div className="py-2">
                  <button
                    type="button"
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    onClick={() => {
                      setMobileUserMenuOpen(false);
                      setIsProfileOpen(true);
                    }}
                  >
                    <User size={16} className="text-gray-400 shrink-0" />
                    {t("label-profile")}
                  </button>

                  <div className="mx-4 h-px bg-gray-100" />

                  <button
                    type="button"
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-blue-600 hover:bg-blue-50 transition-colors"
                    onClick={() => {
                      setMobileUserMenuOpen(false);
                      handleLogout();
                    }}
                  >
                    <LogOut size={16} className="shrink-0" />
                    {t("btn-logout")}
                  </button>
                </div>
              </div>
            </div>
          )}

          <NavUserProfileDrawer
            user={user}
            open={isProfileOpen}
            onOpenChange={setIsProfileOpen}
            onLogout={handleLogout}
          />
        </div>}
      </div>

      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[1.5px] bg-[linear-gradient(0deg,rgba(255,255,255,.75),rgba(255,255,255,.75)),linear-gradient(270deg,rgba(219,178,158,.45)0%,rgba(219,189,172,.45)20.19%,rgba(210,195,187,.45)38.94%,rgba(175,187,207,.45)63.94%,rgba(134,149,180,.45)100%)]"
      />
    </header>
  );
};

export default AppHeader;
